import React from 'react'

import IconButton from '@material-ui/core/IconButton'
import InputAdornment from '@material-ui/core/InputAdornment'
import Paper from '@material-ui/core/Paper'
import { withStyles } from '@material-ui/core/styles'
import TextField from '@material-ui/core/TextField'
import IconClearText from '@material-ui/icons/Close'
import IconSearch from '@material-ui/icons/Search'
import debounce from 'lodash/debounce'
import get from 'lodash/get'
import PropTypes from 'prop-types'
import { batch, connect } from 'react-redux'

import * as PoziUtils from 'api/utils'
import { setGroupCollapsed, setGroupExpanded, setLayerFilter, setLayerSelected, setLayerUnselected } from 'drawer/tabs/layers/actions'
import { setTabLayersFilter } from 'drawer/tabs/layers/search/actions'
import ResetDefaultsButton from 'drawer/tabs/layers/search/ResetDefaultsButton'
import ToggleAllGroupsButton from 'drawer/tabs/layers/search/ToggleAllGroupsButton'
import PoziMap from 'map/map'
import IsSmallScreen from 'utils/IsSmallScreen'

const KEYCODE_ENTER = 13 // https://www.javascriptkeycode.com/

const styles = theme => ({
  root: {},
  form: {
    display: 'flex'
  },
  textField: {
    background: 'white',
    flex: 1,
    marginLeft: 12,
    marginRight: theme.spacing(1),
    marginBottom: 8
  },
  paper: {
    background: '#fafafa',
    borderBottom: '1px solid #dadada'
  },
  iconSearch: {
    color: '#757575'
  },
  iconClearText: {
    color: '#757575'
  }
})

class LayerSearch extends React.Component {
  constructor(props) {
    super(props)

    const { tabLayersFilter, numLayersAllGroupsCollapse } = this.props
    this.state = {
      filterStr: tabLayersFilter
    }

    // Pre-build static UI elements to avoid costly re-renders. e.g. icons, buttons, tooltips, etc.
    this.pure = {}
    this.pure.minCharsRequired = 1
    // const state = window.store.getState()
    // const layercount = get(state, 'config.layercount', 0)
    // if (layercount > numLayersAllGroupsCollapse) {
    //   this.pure.minCharsRequired = 3
    // }

    this.pure.resetDefaultsFunction = this.resetDefaults.bind(this)
    const resetDefaultsButton = <ResetDefaultsButton resetDefaults={this.pure.resetDefaultsFunction} />
    if (IsSmallScreen()) {
      this.pure.resetDefaultsButton = null
    } else {
      this.pure.resetDefaultsButton = resetDefaultsButton
    }

    this.pure.toggleAllGroupsFunction = this.toggleAllGroups.bind(this)
    this.pure.toggleAllGroupsMarkup = <ToggleAllGroupsButton toggleAllGroups={this.pure.toggleAllGroupsFunction} />
    this.pure.otherControls = (
      <span>
        {this.pure.toggleAllGroupsMarkup}
        {this.pure.resetDefaultsButton}
      </span>
    )
  }

  buildSearchBoxAddornment() {
    const { classes, tabLayersFilter } = this.props
    const { filterStr } = this.state

    return {
      inputProps: {
        'data-lpignore': 'true'
      },
      startAdornment: (
        <InputAdornment position="start">
          <IconSearch className={classes.iconSearch} />
        </InputAdornment>
      ),
      endAdornment: filterStr && (
        <InputAdornment position="end">
          <IconButton edge="end" size="small" aria-label="clear search" onClick={this.clearSearch.bind(this)} onMouseDown={this.handleMouseDownPassword} title="Clear search">
            <IconClearText className={classes.iconClearText} />
          </IconButton>
        </InputAdornment>
      )
    }
  }

  setFilter = (filterStr) => {
    const { dispatchSetTabLayersFilter } = this.props

    // console.debug('setFilter', filterStr)

    // Clear search
    if (filterStr.length === 0) {
      dispatchSetTabLayersFilter('')
      return
    }

    // Ignore setting filter if we don't meet the min chars requirement
    if (filterStr.length < this.pure.minCharsRequired) {
      return
    }

    // Actually store the search string
    dispatchSetTabLayersFilter(filterStr)

    // And report to GA
    PoziUtils.gtag({
      category: 'useraction',
      status: 'success',
      identifier: filterStr,
      action: 'Layer Search Input'
    })
  }

  // Avoid updating filter on every keystroke when typing quickly
  setFilterDebounced = debounce(this.setFilter, 300)

  handleInputBoxChange = (event) => {
    const filterStr = event.target.value
    // console.debug('handleInputBoxChange', event.target.value)

    this.setState({ filterStr })
    this.setFilterDebounced(filterStr || '')
  }

  allGroupsAreClosed() {
    const { groups } = this.props

    const groupsArray = Object.values(groups)
    const allClosed = !groupsArray.some((group) => group.expanded)

    return allClosed
  }

  expandAllGroups() {
    const { groups, setGroupExpanded } = this.props

    const groupsArray = Object.values(groups)
    setGroupExpanded(groupsArray)
  }

  collapseAllGroups() {
    const { groups, dispatchSetGroupCollapsed } = this.props

    const groupsArray = Object.values(groups)
    dispatchSetGroupCollapsed(groupsArray)
  }

  toggleAllGroups() {
    let expanded
    if (this.allGroupsAreClosed()) {
      this.expandAllGroups()
      expanded = true
    } else {
      this.collapseAllGroups()
      expanded = false
    }

    PoziUtils.gtag({
      category: 'useraction',
      status: 'success',
      identifier: 'GROUPS',
      action: `expansion panel toggle ${expanded ? 'expand' : 'collapse'} all`
    })
  }

  resetDefaults() {
    const { layers, dispatchSetLayerFilter, dispatchSetLayerSelected, dispatchSetLayerUnselected } = this.props

    this.clearSearch()

    batch(() => {
      for (const key in layers) {
        const layer = layers[key]
        const titleid = layer.titleid
        if (layer.propsFilter) {
          dispatchSetLayerFilter(titleid, null) // Clear layer filter in redux

          PoziMap().showFeaturesInLayer(layer, '', '') // make all features visible again
        }

        if (get(layer, 'selectedAtLoad', false)) {
          if (!layer.selected) {
            dispatchSetLayerSelected(titleid)
          }
        } else {
          if (layer.selected) {
            // Do not unselect basemap layers as we do not want them to be switched off
            const isBasemapLayer = layer.groupid === 'basemap'
            if (isBasemapLayer) {
              continue
            }

            dispatchSetLayerUnselected(titleid)
          }
        }
      }
    })

    this.collapseAllGroups()
  }

  clearSearch() {
    const { filterStr: prevFilterStr } = this.state

    this.setState({ filterStr: '' })
    this.setFilter('')

    // Report to GA
    PoziUtils.gtag({
      category: 'useraction',
      status: 'success',
      identifier: prevFilterStr,
      action: 'Layer Search Clear'
    })
  }

  // https://github.com/mui-org/material-ui/pull/16672
  handleMouseDownPassword(event) {
    event.preventDefault()
  }

  componentDidMount() {
    const tabLayerFilter = document.getElementById('tab-layers-filter')

    if (!tabLayerFilter) {
      return
    }

    tabLayerFilter.onkeydown = function (e) {
      if (e.keyCode === KEYCODE_ENTER) {
        return false // Do not propagate the enter event.
      }
    }
  }

  render() {
    const { filterStr } = this.state
    const { classes, printUIVisible } = this.props

    // Hide the layer search when we are in print mode
    if (printUIVisible) {
      return null
    }

    const textFieldControls = this.buildSearchBoxAddornment()
    const otherControls = this.pure.otherControls

    const textField = (
      <TextField
        id="tab-layers-filter"
        name="tab-layers-filter"
        placeholder="Find layers"
        label=""
        autoComplete="off"
        spellCheck="false"
        margin={'dense'}
        className={classes.textField}
        value={filterStr}
        onChange={this.handleInputBoxChange}
        variant="outlined"
        InputProps={textFieldControls}
      />
    )

    return (
      <div className={classes.root}>
        <Paper className={classes.paper} square={true}>
          <form className={classes.form} noValidate autoComplete="off">
            {textField}
            {otherControls}
          </form>
        </Paper>
      </div>
    )
  }
}

LayerSearch.propTypes = {
  classes: PropTypes.object.isRequired,
  dispatchSetGroupCollapsed: PropTypes.func.isRequired,
  dispatchSetLayerFilter: PropTypes.func.isRequired,
  dispatchSetLayerSelected: PropTypes.func.isRequired,
  dispatchSetLayerUnselected: PropTypes.func.isRequired,
  dispatchSetTabLayersFilter: PropTypes.func.isRequired,
  groups: PropTypes.object.isRequired,
  layers: PropTypes.object.isRequired,
  numLayersAllGroupsCollapse: PropTypes.number.isRequired,
  printUIVisible: PropTypes.bool,
  setGroupExpanded: PropTypes.func.isRequired,
  tabLayersFilter: PropTypes.string.isRequired
}

function mapStateToProps(state, ownProps) {
  const { groups, layers, printUIVisible, tabLayersFilter } = state

  const numLayersAllGroupsCollapse = get(state, 'config.layout.drawer.tabs.layers.numLayersAllGroupsCollapse', 40) // See templates.json

  return {
    groups,
    layers,
    numLayersAllGroupsCollapse,
    printUIVisible,
    tabLayersFilter
  }
}

const mapDispatchToProps = dispatch => {
  return {
    dispatchSetTabLayersFilter: tabLayersFilter => {
      dispatch(setTabLayersFilter(tabLayersFilter))
    },
    dispatchSetGroupCollapsed: group => {
      dispatch(setGroupCollapsed(group))
    },
    setGroupExpanded: group => {
      dispatch(setGroupExpanded(group))
    },
    dispatchSetLayerUnselected: titleid => {
      dispatch(setLayerUnselected(titleid))
    },
    dispatchSetLayerSelected: titleid => {
      dispatch(setLayerSelected(titleid))
    },
    dispatchSetLayerFilter: (titleid, filter) => {
      dispatch(setLayerFilter(titleid, filter))
    }
  }
}

export default (
  withStyles(styles)(
    connect(
      mapStateToProps,
      mapDispatchToProps
    )(LayerSearch)
  )
)
